<template>
  <Listbox
    as="div"
    :modelValue="selectedOptions"
    @update:modelValue="selectedOptions = $event"
    class="w-100 position-relative"
    by="value"
    multiple
    :disabled="disabled"
    v-slot="{ open: isListboxOpen }"
  >
    <ListboxOpenWatcher :open="isListboxOpen" @opened="snapshotSelection" />

    <Float
      as="div"
      placement="bottom-start"
      :offset="5"
      flip
      shift
      adaptiveWidth
      :portal="portal"
      enter="transition duration-200 ease-out"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave="transition duration-75 ease-in"
      leave-from="opacity-100"
      leave-to="opacity-0"
      :show="isListboxOpen"
    >
      <div class="position-relative">
        <ListboxButton
          :id="buttonId"
          class="form-control d-flex align-items-center justify-content-between"
          :class="{
            'form-control-sm': size === 'sm',
          }"
        >
          <span v-if="selectedOptions.length > 0" class="text-truncate">
            {{ buttonLabel }}
          </span>
          <span v-else class="text-muted">
            {{ placeholderComputed }}
          </span>
          <FontAwesomeIcon
            icon="fas fa-chevron-down"
            class="ms-2 flex-shrink-0"
            :class="{ 'rotate-180': isListboxOpen }"
          />
        </ListboxButton>
      </div>

      <ListboxOptions
        class="w-100 overflow-x-hidden overflow-y-auto text-break bg-white border rounded-2 shadow-sm p-0 mt-1"
        style="max-height: 300px"
        @keydown.esc.stop.prevent="handleEscape"
      >
        <div
          v-if="isLoading"
          class="px-3 py-2 text-muted d-flex align-items-center justify-content-center"
        >
          <LoadingIndicatorComponent inline :message="$t('common.loading')" />
        </div>
        <div
          v-else-if="isError"
          class="px-3 py-2 text-muted d-flex align-items-center justify-content-center"
        >
          <FontAwesomeIcon icon="fas fa-exclamation-triangle" class="me-2" />
          <span>{{ $t("common.unexpected_error") }}</span>
        </div>
        <div v-else-if="options.length > 0">
          <!-- Search input -->
          <div class="border-bottom px-3 py-2">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control form-control-sm"
              :placeholder="$t('common.search')"
              :aria-label="$t('common.search')"
              @click.stop
            />
          </div>

          <!-- Clear All / Select All buttons (meaningless when a single-select adapter
               wraps this component — bulk emissions would collapse to one value) -->
          <div
            v-if="!hideBulkActions"
            class="border-bottom px-2 py-1 d-flex justify-content-between gap-1"
          >
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="selectAllFiltered"
              :disabled="
                isLoading ||
                isError ||
                selectableFilteredOptions.length === 0 ||
                selectableFilteredOptions.every((opt) =>
                  modelValueSet.has(opt.value)
                )
              "
            >
              {{ $t("common.select_all") }}
            </button>
            <!-- Caller presets that set the whole selection at once, e.g. a standard set of countries. -->
            <slot name="bulk-actions" />
            <button
              type="button"
              class="btn btn-sm btn-ghost"
              @click="clearAll"
              :disabled="selectedOptions.length === 0"
            >
              {{ $t("common.clear_all") }}
            </button>
          </div>

          <ListboxOption
            v-for="option in filteredOptions"
            :key="option.value"
            :value="option"
            :disabled="option.disabled"
            as="template"
            #default="{ active, selected }"
          >
            <li
              class="select-option-item d-flex align-items-center px-3 py-2"
              :class="{
                'bg-light': active,
                'cursor-pointer': !option.disabled,
                'text-body-tertiary': option.disabled,
              }"
              tabindex="-1"
            >
              <div class="flex-grow-1">
                <slot
                  name="option"
                  :option="option"
                  :active="active"
                  :selected="selected"
                >
                  <WordHighlighter
                    v-if="searchQuery"
                    :query="searchQuery"
                    :textToHighlight="option.label"
                    highlightClass="text-primary p-0"
                    wrapperTag="div"
                    wrapperClass="fw-medium text-wrap"
                  />
                  <div v-else class="fw-medium text-wrap">
                    {{ option.label }}
                  </div>
                  <div class="text-muted small" v-if="option.description">
                    {{ option.description }}
                  </div>
                </slot>
              </div>
              <span v-if="selected" class="ms-2 text-primary">
                <FontAwesomeIcon icon="fas fa-check" />
              </span>
            </li>
          </ListboxOption>

          <div
            v-if="filteredOptions.length === 0 && searchQuery"
            class="px-3 py-2 text-muted"
          >
            <slot name="empty">{{ $t("common.no_results") }}</slot>
          </div>
        </div>
        <div v-else class="px-3 py-2 text-muted">
          <slot name="empty">{{ $t("common.no_results") }}</slot>
        </div>
      </ListboxOptions>
      <span class="visually-hidden" aria-live="polite">{{
        selectionAriaText
      }}</span>
    </Float>
  </Listbox>
</template>

<script setup lang="ts" generic="T extends string | number = string | number">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { Float } from "@headlessui-float/vue";
import { Listbox, ListboxButton, ListboxOption } from "@headlessui/vue";
import { ListboxOptions } from "@/components/common/headlessUi";
import { useDebounce } from "@vueuse/core";
import fuzzysort from "fuzzysort";
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import WordHighlighter from "vue-word-highlighter";
import {
  mergeSelectionPreservingUnknown,
  sortSelectedFirst as sortBySnapshot,
} from "@/components/common/multiSelectUtils";

interface Option<V extends string | number> {
  label: string;
  value: V;
  description?: string;
  /** Shown but not toggleable, and left out of Select All; a selected one stays selected. */
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    options: Option<T>[];
    placeholder?: string;
    size?: "sm" | "md";
    isLoading?: boolean;
    isError?: boolean;
    selectedFirst?: boolean;
    maxDisplayLabels?: number;
    /** Teleport the dropdown panel to <body> — needed inside overflow-clipping
     * containers such as table cells, where the panel would otherwise be cut off. */
    portal?: boolean;
    /** Hide the Select All / Clear All bulk actions. For callers adapting this component
     * to single-select semantics (one-element array, last pick wins), where bulk
     * selection would silently collapse to an arbitrary value. Selection itself stays
     * multi-capable — this only removes the bulk buttons. */
    hideBulkActions?: boolean;
    /** Id for the trigger button, so an external <label for=...> can be associated. */
    buttonId?: string;
    /** The selection is shown but the dropdown does not open. */
    disabled?: boolean;
  }>(),
  {
    size: "md",
    isLoading: false,
    isError: false,
    selectedFirst: false,
    maxDisplayLabels: 0,
    portal: false,
    hideBulkActions: false,
    disabled: false,
  }
);

const model = defineModel<T[]>({ required: true });

const searchQuery = ref("");
const { t } = useI18n();
const debouncedQuery = useDebounce(searchQuery, 200);

const placeholderComputed = computed(
  () => props.placeholder ?? t("common.select")
);

const buttonLabel = computed(() => {
  const opts = selectedOptions.value;
  if (props.maxDisplayLabels <= 0) {
    return opts.length === 1
      ? opts[0].label
      : `${opts.length} ${t("common.selected")}`;
  }
  const suffix = `(${opts.length} ${t("common.selected")})`;
  if (opts.length <= props.maxDisplayLabels) {
    return `${opts.map((o) => o.label).join(", ")} ${suffix}`;
  }
  const visible = opts.slice(0, props.maxDisplayLabels).map((o) => o.label);
  return `${visible.join(", ")} +${opts.length - props.maxDisplayLabels} ${suffix}`;
});

const modelValueSet = computed<Set<T>>(() => new Set(model.value));
const optionMap = computed<Map<T, Option<T>>>(() => {
  const m = new Map<T, Option<T>>();
  for (const opt of props.options) m.set(opt.value, opt);
  return m;
});

const optionValueSet = computed<Set<T>>(
  () => new Set(props.options.map((opt) => opt.value))
);

// Snapshot of the selection taken when the dropdown opens; used for
// selected-first ordering so options don't reorder mid-interaction.
const selectionSnapshot = ref<Set<T>>(new Set());

const snapshotSelection = () => {
  selectionSnapshot.value = new Set(model.value);
};

// Renderless bridge: HeadlessUI only exposes open state via the slot, so a
// tiny component watches it and reports the closed->open transition.
const ListboxOpenWatcher = defineComponent({
  props: { open: { type: Boolean, required: true } },
  emits: ["opened"],
  setup(props, { emit }) {
    watch(
      () => props.open,
      (isOpen, wasOpen) => {
        if (isOpen && !wasOpen) emit("opened");
      }
    );
    return () => null;
  },
});

const sortSelectedFirst = (opts: Option<T>[]): Option<T>[] => {
  if (!props.selectedFirst) return opts;
  return sortBySnapshot(opts, selectionSnapshot.value as Set<T>);
};

const filteredOptions = computed(() => {
  if (!debouncedQuery.value) {
    return sortSelectedFirst(props.options);
  }
  const results = fuzzysort.go(debouncedQuery.value as string, props.options, {
    keys: ["label", "description"],
    threshold: 0,
    // Filtering and Select All must include every match, beyond fuzzysort's default ten.
    limit: Infinity,
  });
  return sortSelectedFirst(results.map((result) => result.obj));
});

const selectedOptions = computed<Option<T>[]>({
  get() {
    const set = modelValueSet.value;
    const result: Option<T>[] = [];
    for (const value of set) {
      const opt = optionMap.value.get(value);
      if (opt) result.push(opt);
    }
    return result;
  },
  set(options: Option<T>[]) {
    model.value = mergeSelectionPreservingUnknown(
      model.value,
      optionValueSet.value,
      options.map((opt) => opt.value)
    );
  },
});

const selectableFilteredOptions = computed(() =>
  filteredOptions.value.filter((opt) => !opt.disabled)
);

const selectAllFiltered = () => {
  const filteredValues = selectableFilteredOptions.value.map(
    (opt) => opt.value
  );
  model.value = [...new Set([...model.value, ...filteredValues])];
};

const clearAll = () => {
  model.value = model.value.filter(
    (value) => optionMap.value.get(value)?.disabled === true
  );
};

const handleEscape = () => {
  searchQuery.value = "";
};

const selectionAriaText = computed(() => {
  const count = selectedOptions.value.length;
  if (count === 0) return "";
  return `${count} ${t("common.selected")}`;
});
</script>

<style lang="scss" scoped>
.select-option-item {
  position: relative;
  z-index: 1;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}
</style>
